import api from "./axios";
import { buildUangJalanReportDownloadFilename } from "../utils/uangJalanReportDownloadFilename";
import {
  type CreateUangJalanReportPayload,
  type UpdateUangJalanReportOrdersPayload,
  type UangJalanReport,
  type UangJalanReportSearchFilters,
  mapUangJalanReportResponse,
  mapUangJalanReportsResponse,
} from "../types/uangJalanReport";
import {
  type PaginationParam,
  type PaginationResponse,
  mapPaginationResponse,
} from "../types/paginationResponse";
import { buildPaginationQuery } from "../utils/pagination";

export interface SearchUangJalanReportsResponse {
  uangJalanReports: UangJalanReport[];
  pagination: PaginationResponse;
}

export const searchUangJalanReportsApi = async (
  filters?: UangJalanReportSearchFilters,
  pagination?: PaginationParam,
): Promise<SearchUangJalanReportsResponse> => {
  try {
    const queryString = buildPaginationQuery(pagination);
    const url = `/api/v1/uangJalanReports/search${queryString}`;
    const response = await api.post<any>(url, filters ?? {});
    return {
      uangJalanReports: mapUangJalanReportsResponse(response.data),
      pagination: mapPaginationResponse(response.data.data.pagination),
    };
  } catch (error) {
    console.error("Error searching uang jalan reports:", error);
    throw error;
  }
};

export const createUangJalanReportApi = async (
  data: CreateUangJalanReportPayload,
): Promise<UangJalanReport> => {
  try {
    const response = await api.post<any>("/api/v1/uangJalanReports", data);
    return mapUangJalanReportResponse(response.data.data.uangJalanReport);
  } catch (error) {
    console.error("Error creating uang jalan report:", error);
    throw error;
  }
};

export const getUangJalanReportByIdApi = async (reportId: string): Promise<UangJalanReport> => {
  try {
    const response = await api.get<any>(`/api/v1/uangJalanReports/${reportId}`);
    return mapUangJalanReportResponse(response.data.data.uangJalanReport);
  } catch (error) {
    console.error("Error fetching uang jalan report:", error);
    throw error;
  }
};

export const verifyUangJalanReportApi = async (reportId: string): Promise<UangJalanReport> => {
  try {
    const response = await api.post<any>(`/api/v1/uangJalanReports/${reportId}/verify`, {});
    return mapUangJalanReportResponse(response.data.data.uangJalanReport);
  } catch (error) {
    console.error("Error verifying uang jalan report:", error);
    throw error;
  }
};

export const approveUangJalanReportApi = async (reportId: string): Promise<UangJalanReport> => {
  try {
    const response = await api.post<any>(`/api/v1/uangJalanReports/${reportId}/approve`, {});
    return mapUangJalanReportResponse(response.data.data.uangJalanReport);
  } catch (error) {
    console.error("Error approving uang jalan report:", error);
    throw error;
  }
};

export const requestUangJalanReportRevisionApi = async (
  reportId: string,
  note: string,
): Promise<UangJalanReport> => {
  try {
    const response = await api.post<any>(`/api/v1/uangJalanReports/${reportId}/requestRevision`, {
      note,
    });
    return mapUangJalanReportResponse(response.data.data.uangJalanReport);
  } catch (error) {
    console.error("Error requesting uang jalan report revision:", error);
    throw error;
  }
};

export const submitUangJalanReportForReviewApi = async (
  reportId: string,
): Promise<UangJalanReport> => {
  try {
    const response = await api.post<any>(
      `/api/v1/uangJalanReports/${reportId}/submitForReview`,
      {},
    );
    return mapUangJalanReportResponse(response.data.data.uangJalanReport);
  } catch (error) {
    console.error("Error submitting uang jalan report for review:", error);
    throw error;
  }
};

export const updateUangJalanReportOrdersApi = async (
  reportId: string,
  data: UpdateUangJalanReportOrdersPayload,
): Promise<UangJalanReport> => {
  try {
    const response = await api.put<any>(`/api/v1/uangJalanReports/${reportId}`, data);
    return mapUangJalanReportResponse(response.data.data.uangJalanReport);
  } catch (error) {
    console.error("Error updating uang jalan report orders:", error);
    throw error;
  }
};

export const normalizeBlobApiError = async (error: unknown): Promise<unknown> => {
  const axiosError = error as { response?: { data?: unknown } };
  const data = axiosError.response?.data;

  if (!(data instanceof Blob)) {
    return error;
  }

  const isJsonBlob = data.type.includes("json") || data.type === "application/octet-stream";
  if (!isJsonBlob) {
    return error;
  }

  try {
    const text = await data.text();
    const parsed = JSON.parse(text) as unknown;
    return {
      ...axiosError,
      response: {
        ...axiosError.response,
        data: parsed,
      },
    };
  } catch {
    return error;
  }
};

const parseContentDispositionFilename = (header: string | undefined): string | undefined => {
  if (!header) {
    return undefined;
  }

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = header.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim();
};

export const downloadUangJalanReportExcelApi = async (
  readableId: string,
  options?: { timezone?: string | null; downloadedAt?: Date },
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response = await api.get<Blob>(`/api/v1/uangJalanReports/${readableId}/excel`, {
      responseType: "blob",
    });
    const filename =
      parseContentDispositionFilename(response.headers["content-disposition"]) ??
      buildUangJalanReportDownloadFilename(readableId, options?.timezone, options?.downloadedAt);
    return { blob: response.data, filename };
  } catch (error) {
    console.error("Error downloading uang jalan report excel:", error);
    throw await normalizeBlobApiError(error);
  }
};
